/* @flow */

import React, { Component } from 'react';
import numeral from 'numeral';
import { connect } from 'react-redux';
import glamorous from 'glamorous';
import { padding } from 'polished';
import Tappable from 'app/components/commons/Tappable';
import { hover } from 'app/utils/cssUtils';
import t from 'app/utils/translate';
import {
  startMyLiveChallengesPoll,
  stopMyLiveChallengesPoll
} from 'app/actions/myChallengesActions';
import CurrencyIcon from 'app/components/commons/CurrencyIcon';
import RewriteLink from 'app/components/routing/RewriteLink';
import useSheet from '../../jss';
import liveChallengesBoardSelector from '../../selectors/liveChallengesBoardSelector';
import LiveChallengeRow from './LiveChallengeRow';

type Props = {
  challengeId?: string,
  challenges: Array<*>,
  classes: Object,
  maxLength?: number,
  numEntries: number,
  startPolling: (?boolean) => void,
  stopPolling: () => void,
  totalPayout?: number,
  totalStake: number
};

const Board = glamorous.div({
  maxWidth: 210,
  width: '12vw'
});

const Title = glamorous.div(({ theme }) => ({
  color: theme.color.white,
  fontSize: theme.fontSize.small,
  fontWeight: theme.fontWeight.demiBold,
  ...padding(theme.spacing.small),
  textTransform: 'uppercase',
  backgroundColor: theme.color.primary,
  textAlign: 'center'
}));

const RowsContainer = glamorous.div(({ theme }) => ({
  marginTop: theme.spacing.small
}));

const Info = glamorous.div(({ theme }) => ({
  color: theme.color.white,
  padding: theme.spacing.small,
  fontSize: theme.fontSize.xsmall
}));

const DataRow = glamorous.div(
  {
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: '1px solid'
  },
  ({ theme }) => ({
    borderColor: theme.color.basis2,
    marginBottom: theme.spacing.tiny
  })
);

const Name = glamorous.span(({ theme }) => ({
  color: theme.color.basis6
}));

const Value = glamorous.span(({ theme, highlight }) => ({
  color: highlight ? theme.color.primary : theme.color.white
}));

class LiveChallengesBoard extends Component<Props, State> {
  props: Props;

  static defaultProps = {
    totalPayout: 0,
    challengeId: undefined,
    maxLength: 5
  };

  componentDidMount() {
    this.props.startPolling();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // business requirement to have my challenges and the challenge
    // results in sync. So if we load a challenge in, restart the poll
    // so this poll and the challenge poll should now be in sync
    if (this.props.challengeId !== nextProps.challengeId) {
      this.props.stopPolling();
      this.props.startPolling(true);
    }
  }

  componentWillUnmount() {
    this.props.stopPolling();
  }

  render() {
    const { challenges, classes, maxLength, numEntries, totalPayout, totalStake } = this.props;

    if (challenges.length === 0) return null;
    return (
      <Board>
        <Title>{t('My Live Challenges')}</Title>
        <Info>
          <DataRow>
            <Name>Entries</Name>
            <Value>{numEntries}</Value>
          </DataRow>
          <DataRow>
            <Name>In Play</Name>
            <Value>
              <CurrencyIcon />
              {numeral(totalStake).format('0,0.[00]')}
            </Value>
          </DataRow>
          <DataRow>
            <Name>Winning</Name>
            <Value highlight>
              <CurrencyIcon />
              {numeral(totalPayout).format('0,0.[00]')}
            </Value>
          </DataRow>
        </Info>
        <RowsContainer>
          {challenges.slice(0, maxLength).map(challenge => (
            <LiveChallengeRow
              key={challenge.id}
              name={challenge.name}
              link={challenge.link}
              position={challenge.rank}
              points={challenge.score}
              status={challenge.statusId}
              winnings={challenge.winnings}
            />
          ))}
        </RowsContainer>
        {challenges.length > maxLength && (
          <div className={classes.button}>
            <Tappable to="/my-challenges" component={RewriteLink} className={classes.buttonHollow}>
              {t('View all')}
            </Tappable>
          </div>
        )}
      </Board>
    );
  }
}

function applyStyles(theme: Theme) {
  return {
    nochallenges: {
      color: theme.color.basis5,
      fontSize: theme.fontSize.tiny,
      ...padding(theme.spacing.tiny, theme.spacing.small),
      textAlign: 'center',
      textTransform: 'uppercase'
    },
    button: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      margin: theme.spacing.small
    },
    buttonHollow: {
      borderColor: theme.color.basis5,
      borderRadius: 50,
      borderStyle: 'solid',
      borderWidth: 2,
      color: theme.color.basis5,
      fontSize: theme.fontSize.tiny,
      ...padding(theme.spacing.tiny, theme.spacing.default),
      textAlign: 'center',
      textTransform: 'uppercase',
      transition: 'background-color 0.2s',
      ...hover({
        backgroundColor: theme.color.basis5,
        color: theme.color.basis1,
        textDecoration: 'none'
      })
    }
  };
}

function mapStateToProps(state: GlobalState) {
  const { numEntries, totalStake, totalPayout, challenges } = liveChallengesBoardSelector(state);
  return {
    challengeId: state.challenge.id,
    challenges,
    numEntries,
    totalStake,
    totalPayout
  };
}

const actions = {
  startPolling: startMyLiveChallengesPoll,
  stopPolling: stopMyLiveChallengesPoll
};

export default useSheet(applyStyles)(
  connect(
    mapStateToProps,
    actions
  )(LiveChallengesBoard)
);
